import React, { useState } from "react";
import PropTypes from "prop-types";

export default function FormFieldsExpander({ formData, requestTypeFields, workflowFormFields }) {
  const [open, setOpen] = useState(true); // open by default so fields are visible immediately

  const fields = buildFields(formData, requestTypeFields, workflowFormFields);
  if (!fields.length) return null;

  return (
    <div className="border rounded" style={{ fontSize: 13 }}>
      <button
        className="btn btn-link w-100 d-flex align-items-center justify-content-between text-decoration-none px-3 py-2"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="fw-semibold text-dark">
          <i className="bi bi-ui-checks me-2" />
          Form Fields ({fields.length})
        </span>
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} text-muted`} />
      </button>
      {open && (
        <div className="px-3 pb-3 border-top">
          <div className="row g-2 mt-1">
            {fields.map((f) => (
              <div key={f.key} className="col-6">
                <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {f.label}
                </div>
                <div className="text-break" style={{ wordBreak: "break-word" }}>
                  {f.value !== undefined && f.value !== null && f.value !== ""
                    ? renderValue(f.value)
                    : <span className="text-muted fst-italic">—</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

FormFieldsExpander.propTypes = {
  formData:           PropTypes.object,
  requestTypeFields:  PropTypes.array,
  workflowFormFields: PropTypes.array,
};

/** Render a value — arrays/objects get flattened to readable strings */
function renderValue(v) {
  if (Array.isArray(v))            return v.join(", ");
  if (typeof v === "object")       return JSON.stringify(v);
  if (typeof v === "boolean")      return v ? "Yes" : "No";
  return String(v);
}

/** Humanise a snake_case or numeric key */
function humanise(k) {
  if (/^\d+$/.test(k)) return `Field ${k}`;
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build label map from both schema sources, then render ALL form_data keys.
 * Internal keys (starting with _) are skipped.
 */
function buildFields(formData, requestTypeFields, workflowFormFields) {
  if (!formData || typeof formData !== "object") return [];

  // Build a label map from every available schema source.
  // Form builder stores data with numeric field_id as keys, so map BOTH
  // the field_id and field_key → label so either format resolves correctly.
  const labelMap = {};
  if (Array.isArray(workflowFormFields)) {
    workflowFormFields.forEach((f) => {
      const label = f.label || humanise(f.field_key || String(f.field_id || ""));
      if (f.field_id)  labelMap[String(f.field_id)]  = label;
      if (f.field_key) labelMap[String(f.field_key)] = label;
    });
  }
  if (Array.isArray(requestTypeFields)) {
    requestTypeFields.forEach((f) => {
      const label = f.label || humanise(f.field_key || "");
      if (f.field_id)  labelMap[String(f.field_id)]  = label;
      if (f.field_key) labelMap[String(f.field_key)] = label;
    });
  }

  // Render ALL form_data entries (skip internal _ keys and null/undefined)
  return Object.entries(formData)
    .filter(([k]) => !k.startsWith("_"))
    .map(([k, v]) => ({
      key:   k,
      label: labelMap[k] || humanise(k),
      value: v,
    }));
}
